import { type QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import type { AutoSendSwitchName, SetGlobalNotice, SetGlobalReportBcc } from '@headline/schemas'
import { api } from './api'

// ── Legacy subscriptions ──────────────────────────────────────────────────

/**
 * The kill switch for automatic sends (CLI-741). Read by everyone — the delivery queue shows a
 * banner while sends are held — and flipped by staff from the Reports page.
 */
export const autoSendSwitchesQuery = queryOptions({
  queryKey: ['auto-send', 'switches'],
  queryFn: async () => {
    const res = await api.api['auto-send'].switches.$get()
    if (!res.ok) throw new Error('Failed to load the automatic-send switches')
    return res.json()
  },
  // A held switch is what everyone is waiting on; keep the answer fresh.
  staleTime: 15_000,
})

export function useSetAutoSendSwitch(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { name: AutoSendSwitchName; paused: boolean; reason?: string }) => {
      const res = await api.api['auto-send'].switches[':name'].$put({
        param: { name: input.name },
        json: { paused: input.paused, ...(input.reason !== undefined && { reason: input.reason }) },
      })
      if (!res.ok) throw new Error('Failed to change the switch')
      return res.json()
    },
    onSuccess: (switches) => {
      queryClient.setQueryData(autoSendSwitchesQuery.queryKey, switches)
    },
  })
}

/**
 * The global notice (CLI-625): the paragraph every report's greeting carries while it is on. Read
 * by anyone signed in — the Settings page previews it — and saved by staff.
 */
export const globalNoticeQuery = queryOptions({
  queryKey: ['settings', 'global-notice'],
  queryFn: async () => {
    const res = await api.api.settings['global-notice'].$get()
    if (!res.ok) throw new Error('Failed to load the global notice')
    return res.json()
  },
  staleTime: 60_000,
})

export function useClearGlobalNotice(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.api.settings['global-notice'].$delete()
      if (!res.ok) throw new Error('Failed to delete the global notice')
      return res.json()
    },
    onSuccess: (notice) => {
      queryClient.setQueryData(globalNoticeQuery.queryKey, notice)
    },
  })
}

/**
 * The global report Bcc (CLI-850). Staff-only on the API, so a caller outside the Settings page
 * reads it through `useGlobalReportBcc`, which never asks for a non-staff session.
 */
export const globalReportBccQuery = queryOptions({
  queryKey: ['settings', 'report-bcc'],
  queryFn: async () => {
    const res = await api.api.settings['report-bcc'].$get()
    if (!res.ok) throw new Error('Failed to load the global report Bcc')
    return res.json()
  },
  staleTime: 60_000,
})

export function useSetGlobalReportBcc(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: SetGlobalReportBcc) => {
      const res = await api.api.settings['report-bcc'].$put({ json: input })
      if (!res.ok) throw new Error('Failed to save the global report Bcc')
      return res.json()
    },
    onSuccess: (bcc) => {
      queryClient.setQueryData(globalReportBccQuery.queryKey, bcc)
    },
  })
}

export function useSetGlobalNotice(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: SetGlobalNotice) => {
      const res = await api.api.settings['global-notice'].$put({ json: input })
      if (!res.ok) throw new Error('Failed to save the global notice')
      return res.json()
    },
    onSuccess: (notice) => {
      queryClient.setQueryData(globalNoticeQuery.queryKey, notice)
    },
  })
}
